import React, { useState, useRef, useEffect, useContext } from 'react';
import GameSummary from './GameSummary';
import { collection, query, where, getDocs, doc, updateDoc, writeBatch, deleteDoc, addDoc } from "firebase/firestore";
import { firestore } from '../firebase';
import { connect } from 'react-redux';
import { fetchGame } from '../util/api/game';
import chessslogo from '../images/chessslogo.png'; // Import chessslogo
import { ChessSocialContext } from '../util/ChessSocialContext'; // Import context

// Add this component at the top level of the file, before the BookmarksTab component
const GameSummaryWithControls = ({ game, folderName, folders, onDelete, onReorder, onMove, gameIndex }) => {
  const [showControls, setShowControls] = useState(false);

  // Handle navigation to the game with bookmark context
  const navigateToGame = () => {
    window.location.href = `/games/${game.gameId}?fromBookmark=true&folderName=${encodeURIComponent(folderName)}&folderIndex=${gameIndex}`;
  };

  // Check if the game has the required data (id, creatorHandle, etc.)
  // if (!game || !game.id || !game.creatorHandle) {
  //   return null; // Don't render if game data is missing
  // }

  // Close all other control panels when this one opens
  useEffect(() => {
    if (showControls) {
      // Publish an event that this control panel is open
      const event = new CustomEvent('gameControlsOpened', { detail: { gameId: game.gameId } });
      document.dispatchEvent(event);
    }
  }, [showControls, game.gameId]);

  // Listen for other control panels opening
  useEffect(() => {
    const handleOtherControlsOpened = (event) => {
      // If another control panel opened and it's not this one, close this one
      if (event.detail.gameId !== game.gameId && showControls) {
        setShowControls(false);
      }
    };

    document.addEventListener('gameControlsOpened', handleOtherControlsOpened);

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('gameControlsOpened', handleOtherControlsOpened);
    };
  }, [game.gameId, showControls]);

  // Process moves data to ensure it's an array
  const processMoves = () => {
    // If moves is a string, try to parse it
    if (typeof game.moves === 'string') {
      try {
        return JSON.parse(game.moves);
      } catch (e) {
        console.error('Error parsing moves string:', e);
        return [];
      }
    }
    // If moves is already an array, use it
    else if (Array.isArray(game.moves)) {
      return game.moves;
    }
    // Otherwise return an empty array
    return [];
  };

  return (
    <div style={{ position: 'relative', marginBottom: '50px', marginTop: '10px' }}>
      {/* Game Summary component - clicking navigates to the game with bookmark context */}
      <div
        onClick={navigateToGame}
        style={{
          cursor: 'pointer',
          borderRadius: '8px',
          overflow: 'visible',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s ease',
          padding: '2px'
        }}
      >
        <GameSummary
          gameData={{
            id: game.gameId,
            creatorHandle: game.creatorHandle,
            caption: game.caption,
            tags: game.tags || [],
            moves: processMoves()
          }}
        />
      </div>

      {/* Ellipsis icon to toggle controls */}
      {!showControls && (
        <div
          className="game-controls-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setShowControls(true);
          }}
          style={{
            position: 'absolute',
            bottom: '-20px',
            left: '50%',
            transform: 'translateX(-50%)',
            cursor: 'pointer',
            color: 'gold',
            fontSize: '24px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.7)',
            border: '2px solid gold',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)',
            zIndex: 5
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateX(-50%) scale(1.1)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.5)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateX(-50%)';
            e.currentTarget.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
          }}
        >
          <i className="fa-solid fa-ellipsis"></i>
        </div>
      )}

      {/* Control bar */}
      {showControls && (
        <div
          className="game-controls"
          style={{
            position: 'absolute',
            bottom: '-55px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '30px',
            padding: '5px',
            border: '2px solid gold',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
            zIndex: 10,
            transition: 'all 0.3s ease'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReorder(game.gameId, folderName, 'left');
            }}
            style={{
              background: 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'gold';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onReorder(game.gameId, folderName, 'right');
            }}
            style={{
              background: 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'gold';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i className="fa-solid fa-arrow-right"></i>
          </button>


          <select
            onChange={(e) => {
              if (e.target.value) {
                onMove(game.gameId, folderName, e.target.value);
                setShowControls(false);
              }
            }}
            style={{
              background: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: '1px solid rgba(255, 215, 0, 0.5)',
              borderRadius: '15px',
              padding: '5px 10px',
              fontSize: '14px',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '12px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = 'gold';
              e.currentTarget.style.boxShadow = '0 0 5px rgba(255, 215, 0, 0.5)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.5)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="">Move to...</option>
            {folders
              .filter(f => f.folderName !== folderName) // Don't show current folder
              .map(f => (
                <option key={f.folderName} value={f.folderName}>
                  {f.folderName} {f.isPrivate ? '(Private)' : '(Public)'}
                </option>
              ))}
          </select>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(game.gameId, folderName);
            }}
            style={{
              background: 'transparent',
              color: '#ff3366',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = '#ff0044';
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 0 8px rgba(255, 0, 68, 0.5)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = '#ff3366';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <i className="fa-solid fa-trash"></i>
          </button>


          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowControls(false);
            }}
            style={{
              background: 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginRight: '5px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'gold';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const BookmarksTab = ({ user }) => {
  const [activeTab, setActiveTab] = useState('public');
  const defaultFolders = [
    { folderName: 'Featured', isPrivate: false, games: [] },
    { folderName: 'Safe', isPrivate: true, games: [] }
  ];
  const [folders, setFolders] = useState(defaultFolders);
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [folderToToggle, setFolderToToggle] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderPrivate, setNewFolderPrivate] = useState(false);
  const [activeGameControls, setActiveGameControls] = useState(null); // Track which game has expanded controls
  const [showFolderDeleteDialog, setShowFolderDeleteDialog] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [activeTabHighlighted, setActiveTabHighlighted] = useState(false);
  const [allGames, setAllGames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [gameIds, setGameIds] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [error, setError] = useState(null);
  const [emptyFolderCount, setEmptyFolderCount] = useState(0); // Track number of empty folders
  const { isSidebarCollapsed } = useContext(ChessSocialContext); // Get context value


  const folderRefs = useRef({});
  const gameRefs = useRef({});
  const scrollContainerRefs = useRef({});

  // Update the useEffect to fetch data when the component mounts
  useEffect(() => {
    getData();
  }, [user]); // Re-fetch when user changes

  const getData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!user || !user.credentials || !user.credentials.userId) {
        setError('You must be logged in to view bookmarks');
        setIsLoading(false);
        return;
      }

      const userId = user.credentials.userId;

      // Fetch bookmark folders for the current user
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId)
      );

      const folderSnapshot = await getDocs(folderQuery);

      let fetchedFolders = [];

      if (!folderSnapshot.empty) {
        // Process folder documents
        fetchedFolders = folderSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Ensure folderIndex exists, default to a high number for new folders
          folderIndex: doc.data().folderIndex !== undefined ? doc.data().folderIndex : 999
        }));
      }

      // Ensure default folders exist
      let mergedFolders = [...fetchedFolders];

      // Check if Featured folder exists, if not add it
      if (!fetchedFolders.some(folder => folder.folderName === 'Featured')) {
        mergedFolders.push({
          folderName: 'Featured',
          isPrivate: false,
          games: [],
          userId: userId,
          folderIndex: 0 // Featured is always index 0 in public folders
        });
      } else {
        // Ensure Featured has index 0
        mergedFolders = mergedFolders.map(folder =>
          folder.folderName === 'Featured' ? { ...folder, folderIndex: 0 } : folder
        );
      }

      // Check if Safe folder exists, if not add it
      if (!fetchedFolders.some(folder => folder.folderName === 'Safe')) {
        mergedFolders.push({
          folderName: 'Safe',
          isPrivate: true,
          games: [],
          userId: userId,
          folderIndex: 0 // Safe is always index 0 in private folders
        });
      } else {
        // Ensure Safe has index 0
        mergedFolders = mergedFolders.map(folder =>
          folder.folderName === 'Safe' ? { ...folder, folderIndex: 0 } : folder
        );
      }

      // Sort folders by folderIndex within their privacy category
      mergedFolders.sort((a, b) => {
        // First sort by privacy
        if (a.isPrivate !== b.isPrivate) {
          return a.isPrivate ? 1 : -1;
        }
        // Then sort by folderIndex
        return a.folderIndex - b.folderIndex;
      });

      
      setFolders(mergedFolders);

      // Count empty folders (excluding default folders)
      const emptyCount = mergedFolders.filter(folder =>
        folder.folderName !== 'Featured' &&
        folder.folderName !== 'Safe' &&
        (!folder.games || folder.games.length === 0)
      ).length;
      setEmptyFolderCount(emptyCount);

      // Fetch game details for all bookmarked games
      const gameIds = new Set();
      mergedFolders.forEach(folder => {
        if (folder.games && Array.isArray(folder.games)) {
          folder.games.forEach(game => {
            gameIds.add(game.gameId);
          });
        }
      });

      

      // Use fetchBatchData to get game details
      if (gameIds.size > 0) {
        await fetchBatchData(Array.from(gameIds));
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      setError('Failed to load bookmarks. Please try again.');
      setIsLoading(false);
    }
  };

  const fetchBatchData = async (gameIds) => {
    if (!gameIds || gameIds.length === 0) return;

    try {
      

      // Fetch game data for all gameIds
      const gamePromises = gameIds.map(gameId => fetchGame(gameId));
      const gameResults = await Promise.allSettled(gamePromises);

      const successfulGames = gameResults
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value)
        // .filter(game => game && !game.error && !game.isNotFound); // Filter out games with errors or missing games

      

      // Store game data in state
      setGamesData(successfulGames);
    } catch (error) {
      console.error('Error fetching game data:', error);
    }
  };

  // Show notification function
  const showNotification = (message, type = 'success') => {
    setNotification({
      message,
      type
    });

    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Function to switch tabs with highlight effect and sync with folder highlighting
  const switchTabWithHighlight = (tab, folderName = null) => {
    setActiveTab(tab);
    setActiveTabHighlighted(true);

    // If we have a folder name, highlight it in sync with the tab
    if (folderName) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const folderElement = folderRefs.current[folderName];
        if (folderElement) {
          // Highlight the folder
          folderElement.style.boxShadow = '0 0 0 3px #ff0050';
          folderElement.style.borderRadius = '10px';
        }

        // Remove both highlights after exactly 3 seconds
        setTimeout(() => {
          setActiveTabHighlighted(false);
          if (folderElement) {
            folderElement.style.boxShadow = 'none';
          }
        }, 3000);
      }, 50);
    } else {
      // If no folder to highlight, just remove tab highlight after 3 seconds
      setTimeout(() => {
        setActiveTabHighlighted(false);
      }, 3000);
    }
  };

  const toggleFolderExpand = (folderName) => {
    if (expandedFolder === folderName) {
      setExpandedFolder(null);
    } else {
      setExpandedFolder(folderName);
    }
  };

  const toggleFolderPrivacy = (folderName) => {
    // Don't allow toggling privacy for default folders
    if (folderName === 'Featured' || folderName === 'Safe') {
      showNotification("Cannot change privacy settings for default folders", "error");
      return;
    }

    const folder = folders.find(f => f.folderName === folderName);
    if (!folder) return;

    if (folder.isPrivate) {
      setFolderToToggle(folderName);
      setShowConfirmDialog(true);
    } else {
      // Update local state
      setFolders(folders.map(f =>
        f.folderName === folderName ? { ...f, isPrivate: true } : f
      ));

      // Update in Firestore
      const folderRef = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", user.credentials.userId),
        where("folderName", "==", folderName)
      );

      getDocs(folderRef)
        .then((snapshot) => {
          if (!snapshot.empty) {
            const docRef = snapshot.docs[0].ref;

            // Get the highest folderIndex in the private category
            const privateMaxIndex = Math.max(
              ...folders
                .filter(f => f.isPrivate && f.folderName !== 'Safe')
                .map(f => f.folderIndex),
              0
            );

            updateDoc(docRef, {
              isPrivate: true,
              folderIndex: privateMaxIndex + 1 // Add to the end of private folders
            })
              .then(() => {
                showNotification(`Folder "${folderName}" is now private`);

                // Update local state with new index
                setFolders(folders.map(f =>
                  f.folderName === folderName ? { ...f, isPrivate: true, folderIndex: privateMaxIndex + 1 } : f
                ));
              })
              .catch((error) => {
                console.error("Error updating folder privacy:", error);
                showNotification("Failed to update folder privacy", "error");
                // Revert local state on error
                setFolders(folders);
              });
          }
        })
        .catch((error) => {
          console.error("Error querying folder:", error);
          showNotification("Failed to update folder privacy", "error");
          // Revert local state on error
          setFolders(folders);
        });
    }
  };

  const confirmMakePublic = () => {
    if (folderToToggle) {
      // Get the highest folderIndex in the public category
      const publicMaxIndex = Math.max(
        ...folders
          .filter(f => !f.isPrivate && f.folderName !== 'Featured')
          .map(f => f.folderIndex),
        0
      );

      // Find the folder document in Firestore
      const folderRef = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", user.credentials.userId),
        where("folderName", "==", folderToToggle)
      );

      getDocs(folderRef)
        .then((snapshot) => {
          if (!snapshot.empty) {
            const docRef = snapshot.docs[0].ref;

            // Update the folder to be public with a new index
            updateDoc(docRef, {
              isPrivate: false,
              folderIndex: publicMaxIndex + 1 // Add to the end of public folders
            })
              .then(() => {
                // Update local state
                setFolders(folders.map(folder =>
                  folder.folderName === folderToToggle
                    ? { ...folder, isPrivate: false, folderIndex: publicMaxIndex + 1 }
                    : folder
                ));

                // Show notification
                showNotification(`Folder "${folderToToggle}" is now public`);

                // Switch to public tab with highlight, synchronized with folder highlight
                switchTabWithHighlight('public', folderToToggle);
              })
              .catch((error) => {
                console.error("Error updating folder privacy:", error);
                showNotification("Failed to update folder privacy", "error");
              });
          }
        })
        .catch((error) => {
          console.error("Error querying folder:", error);
          showNotification("Failed to update folder privacy", "error");
        });

      setFolderToToggle(null);
    }
    setShowConfirmDialog(false);
  };

  const cancelMakePublic = () => {
    setFolderToToggle(null);
    setShowConfirmDialog(false);
  };

  const handleDeleteFolder = (folderName) => {
    setFolderToDelete(folderName);
    setShowFolderDeleteDialog(true);
  };

  const confirmDeleteFolder = async () => {
    if (folderToDelete) {
      try {
        // Find the folder in state
        const folder = folders.find(f => f.folderName === folderToDelete);

        // Don't allow deletion of default folders
        if (folderToDelete === 'Featured' || folderToDelete === 'Safe') {
          showNotification(`Cannot delete default folder "${folderToDelete}"`, 'error');
          setFolderToDelete(null);
          setShowFolderDeleteDialog(false);
          return;
        }

        if (folder && folder.id) {
          // Delete the folder document from Firestore completely
          const folderRef = doc(firestore, "bookmarkFolders", folder.id);
          await deleteDoc(folderRef);
          
        }

        // Remove the folder from state
        const updatedFolders = folders.filter(folder => folder.folderName !== folderToDelete);
        setFolders(updatedFolders);

        // Update empty folder count if needed
        if (!folder.games || folder.games.length === 0) {
          setEmptyFolderCount(prev => Math.max(0, prev - 1));
        }

        // Show notification
        showNotification(`Folder "${folderToDelete}" has been deleted`);

        setFolderToDelete(null);
      } catch (error) {
        console.error('Error deleting folder:', error);
        showNotification('Failed to delete folder. Please try again.', 'error');
      }
    }
    setShowFolderDeleteDialog(false);
  };

  const cancelDeleteFolder = () => {
    setFolderToDelete(null);
    setShowFolderDeleteDialog(false);
  };

  const handleDeleteGame = async (gameId, folderName) => {
    setGameToDelete({ gameId, folderName });
    setShowDeleteDialog(true);
  };

  const confirmDeleteGame = async () => {
    try {
      const { gameId, folderName } = gameToDelete;

      // Find the folder
      const folder = folders.find(f => f.folderName === folderName);
      if (!folder) {
        console.error(`Folder "${folderName}" not found`);
        setShowDeleteDialog(false);
        return;
      }

      // Find the game in the folder
      const gameIndex = folder.games.findIndex(g => g.gameId === gameId);
      if (gameIndex === -1) {
        console.error(`Game ${gameId} not found in folder ${folderName}`);
        setShowDeleteDialog(false);
        return;
      }

      const game = folder.games[gameIndex];

      // Remove the game from the folder
      const updatedGames = [...folder.games];
      updatedGames.splice(gameIndex, 1);

      // Reindex the remaining games
      updatedGames.forEach(g => {
        if (g.index > game.index) {
          g.index -= 1;
        }
      });

      // Only update Firestore if the folder has an ID (is saved in the database)
      if (folder.id) {
        // Update the folder document in Firestore
        const folderRef = doc(firestore, "bookmarkFolders", folder.id);
        await updateDoc(folderRef, {
          games: updatedGames,
          updatedAt: new Date().toISOString()
        });
      }

      // Update local state
      const updatedFolders = folders.map(f => {
        if (f.folderName === folderName) {
          return { ...f, games: updatedGames };
        }
        return f;
      });

      setFolders(updatedFolders);

      showNotification(`Game removed from folder "${folderName}"`);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting game:', error);
      showNotification('Failed to delete game. Please try again.', 'error');
      setShowDeleteDialog(false);
    }
  };

  const cancelDeleteGame = () => {
    setGameToDelete(null);
    setShowDeleteDialog(false);
  };

  // Scroll handlers for game summaries
  const scrollLeft = (folderId) => {
    // Check if the folderId already has the folder- prefix
    const containerKey = folderId.startsWith('folder-') ? folderId : `folder-${folderId}`;
    const container = scrollContainerRefs.current[containerKey];

    if (container) {
      
      container.scrollBy({ left: -250, behavior: 'smooth' });
    } else {
      
      
    }
  };

  const scrollRight = (folderId) => {
    // Check if the folderId already has the folder- prefix
    const containerKey = folderId.startsWith('folder-') ? folderId : `folder-${folderId}`;
    const container = scrollContainerRefs.current[containerKey];

    if (container) {
      
      container.scrollBy({ left: 250, behavior: 'smooth' });
    } else {
      
      
    }
  };

  // Add a function to check for container overflow
  const checkContainerOverflow = (containerId) => {
    const container = scrollContainerRefs.current[`folder-${containerId}`];
    if (!container) return false;

    return container.scrollWidth > container.clientWidth;
  };

  // Add useEffect to handle scroll button visibility
  useEffect(() => {
    if (!expandedFolder) return;

    // Function to check and update arrow visibility based on overflow
    const checkScrollOverflow = () => {
      const folderId = expandedFolder;
      const container = scrollContainerRefs.current[`folder-${folderId}`];

      if (!container) return;

      // Find the left and right arrow buttons for this folder
      const leftArrow = document.querySelector(`.left-scroll-${folderId}`);
      const rightArrow = document.querySelector(`.right-scroll-${folderId}`);

      if (container && leftArrow && rightArrow) {
        const hasOverflow = container.scrollWidth > container.clientWidth;

        // Apply opacity and interactivity based on overflow
        const arrowOpacity = hasOverflow ? 1 : 0;
        leftArrow.style.opacity = arrowOpacity;
        rightArrow.style.opacity = arrowOpacity;
        leftArrow.style.pointerEvents = hasOverflow ? 'auto' : 'none';
        rightArrow.style.pointerEvents = hasOverflow ? 'auto' : 'none';
      }
    };

    // Create a ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(checkScrollOverflow);

    // Get the current expanded folder's container
    const container = scrollContainerRefs.current[`folder-${expandedFolder}`];

    // Observe the container if it exists
    if (container) {
      resizeObserver.observe(container);
    }

    // Also check on window resize
    window.addEventListener('resize', checkScrollOverflow);

    // Run an initial check
    checkScrollOverflow();

    // Run a delayed check to ensure content is fully rendered
    setTimeout(checkScrollOverflow, 300);

    // Cleanup
    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollOverflow);
    };
  }, [expandedFolder, allGames, folders]);

  // Filter folders based on active tab
  const filteredFolders = folders
    .filter(folder =>
      (activeTab === 'public' && !folder.isPrivate) ||
      (activeTab === 'private' && folder.isPrivate)
    )
    // Add sorting here to ensure folders are always displayed in order by folderIndex
    .sort((a, b) => {
      // Keep default folders (Featured, Safe) at the top
      if (a.folderName === 'Featured' || a.folderName === 'Safe') return -1;
      if (b.folderName === 'Featured' || b.folderName === 'Safe') return 1;
      // Sort the rest by folderIndex
      return a.folderIndex - b.folderIndex;
    });

  // Add this useEffect to optimize rendering performance
  useEffect(() => {
    // This effect runs when folders state changes
    // We can add optimizations here if needed

    // For debugging - you can remove this in production
    if (lastUpdateTime) {
      
    }
  }, [folders, lastUpdateTime]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      showNotification("Please enter a folder name", "error");
      return;
    }

    // Check if folder name already exists
    if (folders.some(folder => folder.folderName === newFolderName.trim())) {
      showNotification("A folder with this name already exists", "error");
      return;
    }

    // Count empty folders (folders with no games)
    const emptyFolderCount = folders.filter(folder =>
      folder.games.length === 0 &&
      folder.folderName !== 'Featured' &&
      folder.folderName !== 'Safe'
    ).length;

    // Check if creating an empty folder would exceed the limit
    if (emptyFolderCount >= 3) {
      showNotification("You can only have up to 3 empty folders", "error");
      return;
    }

    try {
      // Get the highest folderIndex in the appropriate category
      const isPrivate = newFolderPrivate;
      const maxIndex = Math.max(
        ...folders
          .filter(f => f.isPrivate === isPrivate &&
            (f.folderName !== 'Featured' && f.folderName !== 'Safe'))
          .map(f => f.folderIndex),
        0
      );

      const newFolder = {
        userId: user.credentials.userId,
        folderName: newFolderName.trim(),
        isPrivate: newFolderPrivate,
        games: [],
        createdAt: new Date().toISOString(),
        folderIndex: maxIndex + 1 // Add to the end of the appropriate category
      };

      // Add to Firestore
      const docRef = await addDoc(collection(firestore, "bookmarkFolders"), newFolder);

      // Update local state - add to the end of the list
      setFolders([...folders, {
        id: docRef.id,
        folderName: newFolderName.trim(),
        isPrivate: newFolderPrivate,
        games: [],
        folderIndex: maxIndex + 1
      }]);

      showNotification(`Folder "${newFolderName.trim()}" created successfully`);
      setNewFolderName("");
      setNewFolderPrivate(false);
      setShowNewFolderInput(false);
    } catch (error) {
      console.error("Error creating folder:", error);
      showNotification("Failed to create folder", "error");
    }
  };

  // Add function to handle reordering games within a folder
  const reorderGames = async (gameId, folderName, direction) => {
    try {
      setIsLoading(true);
      

      // Find the folder in our state by folderName
      const folder = folders.find(f => f.folderName === folderName);

      if (!folder) {
        console.error(`Folder "${folderName}" not found`);
        showNotification(`Folder not found`, 'error');
        setIsLoading(false);
        return;
      }

      // Find the game in the folder
      const games = folder.games || [];
      const gameIndex = games.findIndex(g => g.gameId === gameId);

      if (gameIndex === -1) {
        console.error(`Game ${gameId} not found in folder ${folderName}`);
        showNotification(`Game not found in folder`, 'error');
        setIsLoading(false);
        return;
      }

      // Calculate new index based on direction
      const newIndex = direction === 'left' ? gameIndex - 1 : gameIndex + 1;

      // Check if new index is valid
      if (newIndex < 0 || newIndex >= games.length) {
        
        setIsLoading(false);
        return;
      }

      // Get the Firestore document reference
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", user.credentials.userId),
        where("folderName", "==", folderName)
      );

      const folderSnapshot = await getDocs(folderQuery);

      if (folderSnapshot.empty) {
        console.error(`Folder "${folderName}" not found in database`);
        showNotification(`Folder not found in database`, 'error');
        setIsLoading(false);
        return;
      }

      const folderDoc = folderSnapshot.docs[0];
      const folderData = folderDoc.data();

      // Get the games array from Firestore
      const firestoreGames = folderData.games || [];

      // Sort the games by index to ensure correct order
      firestoreGames.sort((a, b) => a.index - b.index);

      // Find the game to move and the game to swap with
      const gameToMoveIndex = firestoreGames.findIndex(g => g.gameId === gameId);

      if (gameToMoveIndex === -1) {
        console.error(`Game ${gameId} not found in folder ${folderName} in database`);
        showNotification(`Game not found in database`, 'error');
        setIsLoading(false);
        return;
      }

      // Calculate new index in the database
      const newDbIndex = direction === 'left' ? gameToMoveIndex - 1 : gameToMoveIndex + 1;

      // Check if new index is valid
      if (newDbIndex < 0 || newDbIndex >= firestoreGames.length) {
        
        setIsLoading(false);
        return;
      }

      // Get the two games that will swap positions
      const gameToMove = firestoreGames[gameToMoveIndex];
      const gameToSwap = firestoreGames[newDbIndex];

      // console.log('Before swap:', {
      //   gameToMove: { gameId: gameToMove.gameId, index: gameToMove.index },
      //   gameToSwap: { gameId: gameToSwap.gameId, index: gameToSwap.index }
      // });

      // Swap their indices
      const tempIndex = gameToMove.index;
      gameToMove.index = gameToSwap.index;
      gameToSwap.index = tempIndex;

      // console.log('After swap:', {
      //   gameToMove: { gameId: gameToMove.gameId, index: gameToMove.index },
      //   gameToSwap: { gameId: gameToSwap.gameId, index: gameToSwap.index }
      // });

      // Update the database
      await updateDoc(folderDoc.ref, {
        games: firestoreGames,
        updatedAt: new Date().toISOString()
      });

      // Create a deep copy of the folders array
      const updatedFolders = JSON.parse(JSON.stringify(folders));

      // Find the folder to update
      const folderToUpdateIndex = updatedFolders.findIndex(f => f.folderName === folderName);

      if (folderToUpdateIndex !== -1) {
        // Get the games array from the folder
        const updatedGames = updatedFolders[folderToUpdateIndex].games || [];

        // Find the indices of the games to swap in the local state
        const localGameToMoveIndex = updatedGames.findIndex(g => g.gameId === gameId);
        const localGameToSwapIndex = direction === 'left' ? localGameToMoveIndex - 1 : localGameToMoveIndex + 1;

        if (localGameToMoveIndex !== -1 && localGameToSwapIndex >= 0 && localGameToSwapIndex < updatedGames.length) {
          // Swap the games in the local state
          const temp = updatedGames[localGameToMoveIndex];
          updatedGames[localGameToMoveIndex] = updatedGames[localGameToSwapIndex];
          updatedGames[localGameToSwapIndex] = temp;

          // Update the indices to match their new positions
          updatedGames.forEach((game, idx) => {
            game.index = idx;
          });

          // Update the folder with the new games array
          updatedFolders[folderToUpdateIndex].games = updatedGames;

          // Update the state
          setFolders(updatedFolders);
        }
      }

      
      showNotification(`Game reordered successfully`);

    } catch (error) {
      console.error("Error reordering game:", error);
      showNotification(`Error reordering game: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Add function to handle moving a game to a different folder
  const moveGameToFolder = async (gameId, sourceFolder, targetFolder) => {
    try {
      // Find the source folder
      const sourceFolderDoc = folders.find(f => f.folderName === sourceFolder);
      if (!sourceFolderDoc) {
        console.error(`Source folder "${sourceFolder}" not found`);
        return;
      }

      // Find the target folder
      const targetFolderDoc = folders.find(f => f.folderName === targetFolder);
      if (!targetFolderDoc) {
        console.error(`Target folder "${targetFolder}" not found`);
        return;
      }

      // Find the game in the source folder
      const gameIndex = sourceFolderDoc.games.findIndex(g => g.gameId === gameId);
      if (gameIndex === -1) {
        console.error(`Game ${gameId} not found in folder ${sourceFolder}`);
        return;
      }

      const game = sourceFolderDoc.games[gameIndex];

      // Remove the game from the source folder
      const updatedSourceGames = [...sourceFolderDoc.games];
      updatedSourceGames.splice(gameIndex, 1);

      // Reindex the source folder games
      updatedSourceGames.forEach(g => {
        if (g.index > game.index) {
          g.index -= 1;
        }
      });

      // Add the game to the target folder at index 0
      // Ensure targetFolderDoc.games is always an array
      const updatedTargetGames = [...(Array.isArray(targetFolderDoc.games) ? targetFolderDoc.games : [])];

      // Increment all indexes in the target folder
      updatedTargetGames.forEach(g => {
        g.index += 1;
      });

      // Add the game at index 0
      updatedTargetGames.unshift({
        ...game,
        index: 0,
        createdAt: new Date().toISOString()
      });

      // Use a batch for database operations
      const batch = writeBatch(firestore);

      // Update source folder in Firestore
      const sourceFolderRef = doc(firestore, "bookmarkFolders", sourceFolderDoc.id);
      batch.update(sourceFolderRef, {
        games: updatedSourceGames,
        updatedAt: new Date().toISOString()
      });

      // Check if target folder exists in the database
      let targetFolderRef;

      if (!targetFolderDoc.id) {
        // Target folder doesn't exist in the database yet (this would be the case for empty Featured or Safe folders)
        // We need to create it first
        

        // Determine the proper folderIndex (0 for Featured and Safe, which are default folders)
        const folderIndex = (targetFolder === 'Featured' || targetFolder === 'Safe') ? 0 : 1;

        // Create new folder document data
        const newFolderData = {
          userId: user.credentials.userId,
          folderName: targetFolder,
          isPrivate: targetFolder === 'Safe', // Featured is public, Safe is private
          games: updatedTargetGames,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          folderIndex: folderIndex
        };

        // Add the new folder document to Firestore
        const newFolderRef = doc(collection(firestore, "bookmarkFolders"));
        batch.set(newFolderRef, newFolderData);

        // Update the targetFolderRef to the new reference
        targetFolderRef = newFolderRef;

        // Update local state with the new ID
        targetFolderDoc.id = newFolderRef.id;
      } else {
        // Target folder already exists in the database
        targetFolderRef = doc(firestore, "bookmarkFolders", targetFolderDoc.id);
        batch.update(targetFolderRef, {
          games: updatedTargetGames,
          updatedAt: new Date().toISOString()
        });
      }

      // Commit the batch operation
      await batch.commit();

      // Update local state
      const updatedFolders = folders.map(f => {
        if (f.id === sourceFolderDoc.id) {
          return { ...f, games: updatedSourceGames };
        }
        if (f.folderName === targetFolderDoc.folderName) {
          return {
            ...f,
            id: targetFolderDoc.id || targetFolderRef.id,
            games: updatedTargetGames
          };
        }
        return f;
      });

      setFolders(updatedFolders);

      showNotification(`Game moved from "${sourceFolder}" to "${targetFolder}"`);
    } catch (error) {
      console.error('Error moving game to folder:', error);
      showNotification('Failed to move game. Please try again.', 'error');
    }
  };

  // Add function to handle folder reordering
  const changeFolderIndex = async (folderName, direction) => {
    setIsLoading(true);
    try {
      // Find the folder
      const folder = folders.find(f => f.folderName === folderName);
      if (!folder) {
        console.error(`Folder "${folderName}" not found`);
        setIsLoading(false);
        return;
      }

      // Don't allow reordering default folders
      if (folderName === 'Featured' || folderName === 'Safe') {
        showNotification("Cannot reorder default folders", "error");
        setIsLoading(false);
        return;
      }

      // Get all folders in the same privacy category
      const categoryFolders = folders.filter(f =>
        f.isPrivate === folder.isPrivate &&
        f.folderName !== 'Featured' &&
        f.folderName !== 'Safe'
      );

      // Sort by folderIndex
      categoryFolders.sort((a, b) => a.folderIndex - b.folderIndex);

      // Find the current folder's position in the sorted list
      const currentPosition = categoryFolders.findIndex(f => f.folderName === folderName);

      // Calculate the target position
      const targetPosition = direction === 'up' ? currentPosition - 1 : currentPosition + 1;

      // Check if the target position is valid
      if (targetPosition < 0 || targetPosition >= categoryFolders.length) {
        // Already at the top or bottom
        setIsLoading(false);
        return;
      }

      // Get the target folder
      const targetFolder = categoryFolders[targetPosition];

      // Swap indexes
      const currentIndex = folder.folderIndex;
      const targetIndex = targetFolder.folderIndex;

      // Apply immediate UI update first by creating a deep copy of the current folders array
      const updatedFolders = JSON.parse(JSON.stringify(folders)).map(f => {
        if (f.folderName === folderName) {
          return { ...f, folderIndex: targetIndex };
        }
        if (f.folderName === targetFolder.folderName) {
          return { ...f, folderIndex: currentIndex };
        }
        return f;
      });

      // Update local state immediately to reflect the change in UI
      setFolders(updatedFolders);
      // Refresh the component by updating the lastUpdateTime
      setLastUpdateTime(Date.now());

      // Show notification immediately
      showNotification(`Folder "${folderName}" moved ${direction}`);

      // Update in Firestore using a batch
      const batch = writeBatch(firestore);

      // Update current folder
      const currentFolderRef = doc(firestore, "bookmarkFolders", folder.id);
      batch.update(currentFolderRef, { folderIndex: targetIndex });

      // Update target folder
      const targetFolderRef = doc(firestore, "bookmarkFolders", targetFolder.id);
      batch.update(targetFolderRef, { folderIndex: currentIndex });

      // Commit the batch
      await batch.commit();

      setIsLoading(false);
    } catch (error) {
      console.error('Error reordering folders:', error);
      showNotification('Failed to reorder folders. Please try again.', 'error');

      // If error occurs, refresh the data to restore the correct order
      getData();
      setIsLoading(false);
    }
  };

  return (
    <div className="app-container" style={{ overflowX: 'hidden' }}>
      <div className="main-column explore-container" style={{ maxWidth: isSidebarCollapsed ? 'calc(100vw - 125px)' : 'calc(100vw - 250px)',
        width: '100%',
       position: 'relative', background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>

        {/* Simple loading spinner */}
        {isLoading && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '20px',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={chessslogo}
              alt="Loading bookmarks"
              style={{
                width: '60px',
                height: 'auto',
                animation: 'spin 2s linear infinite'
              }}
            />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
           
          </div>
        )}

        {/* Notification popup */}
        {notification && (
          <div style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "15px",
            backgroundColor: 'rgba(0, 0, 0, 1)',
            color: notification.type === 'error' ? '#ff3366' : '#58b047',
            padding: '15px',
            width: '100%',
            maxWidth: '400px',
            borderRadius: '8px',
            zIndex: '1001', // Higher than the Create New Folder div (1000)
            fontSize: '16px',
            fontWeight: 'bold',
            textAlign: 'center',
            maxWidth: '80%',
            minWidth: '300px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
          }}>
            {notification.message}
          </div>
        )}

        {/* Confirmation Dialog for making a folder public */}
        {showConfirmDialog && (
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '2px solid gold',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '400px',
              width: '100%'
            }}>
              <h3 style={{ color: 'gold', marginTop: 0 }}>Make Folder Public?</h3>
              <p style={{ color: 'white', marginBottom: '20px' }}>
                Making this folder public will allow all users to see its contents. Are you sure you want to continue?
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={cancelMakePublic}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid white',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMakePublic}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: 'gold',
                    color: 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Make Public
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog for deleting a game */}
        {showDeleteDialog && (
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '2px solid #ff4d4d',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '400px',
              width: '100%'
            }}>
              <h3 style={{ color: '#ff4d4d', marginTop: 0 }}>Delete Game?</h3>
              <p style={{ color: 'white', marginBottom: '20px' }}>
                Are you sure you want to remove this game from the folder? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={cancelDeleteGame}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid white',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteGame}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#ff4d4d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog for deleting a folder */}
        {showFolderDeleteDialog && (
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '2px solid #ff4d4d',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '400px',
              width: '100%'
            }}>
              <h3 style={{ color: '#ff4d4d', marginTop: 0 }}>Delete Folder?</h3>
              <p style={{ color: 'white', marginBottom: '20px' }}>
                Are you sure you want to delete this folder and all its contents? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={cancelDeleteFolder}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid white',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteFolder}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#ff4d4d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="" style={{ display: 'flex', justifyContent: 'center', borderBottom: '4px solid black', height: '', marginTop: '25px' }}>
          <button
            style={{
              flex: 1,
              padding: '5px',
              paddingBottom: '50px',
              color: activeTab === 'public' ? (activeTabHighlighted && activeTab === 'public' ? '#ff0050' : '#ffd700') : 'white',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'public' ?
                (activeTabHighlighted && activeTab === 'public' ? '4px solid #ff0050' : '4px solid #ffd700') :
                '2px solid white',
              fontSize: '30px'
            }}
            onClick={() => handleTabClick('public')}
          >
            Public
          </button>
          <button
            style={{
              flex: 1,
              padding: '5px',
              paddingBottom: '50px',
              color: activeTab === 'private' ? (activeTabHighlighted && activeTab === 'private' ? '#ff0050' : '#ffd700') : 'white',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'private' ?
                (activeTabHighlighted && activeTab === 'private' ? '4px solid #ff0050' : '4px solid #ffd700') :
                '2px solid white',
              fontSize: '30px'
            }}
            onClick={() => handleTabClick('private')}
          >
            Private
          </button>
        </div>

        {/* Scrollable content area - separate from the tabs */}
        <div style={{
          padding: '20px',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: '90%',
          margin: '0 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: 'calc(100vh - 250px)', // Adjusted to account for header and create folder UI
          paddingBottom: '100px' // Add padding to prevent content from being hidden behind the create folder UI
        }}>
          {/* Folders and Games Display */}
          <div style={{ marginTop: '20px' }}>
            {filteredFolders.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#aaa',
                fontSize: '18px'
              }}>
                No {activeTab} folders found. Bookmark some games to see them here!
              </div>
            ) : (
              filteredFolders.map((folder) => {
                // Sort games by index
                const sortedGames = folder.games && Array.isArray(folder.games)
                  ? [...folder.games].sort((a, b) => a.index - b.index)
                  : [];

                // Map game IDs to full game data, filtering out missing games
                const allGames = sortedGames.map(game => {
                  const gameData = gamesData.find(g => g && g.id === game.gameId);
                  return gameData ? { ...game, ...gameData } : null;
                }).filter(game => game !== null); // Filter out games with missing data

                

                return (
                  <div
                    key={folder.folderName || folder.id}
                    ref={el => folderRefs.current[folder.folderName] = el}
                    style={{
                      marginBottom: '20px',
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid gray',
                      position: 'relative',
                      height: '100%',
                      borderRadius: '5px 5px 0px 0px',
                      maxWidth: '100%',
                      width: '100%',
                    }}
                  >
                    {/* Folder Header */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '10px',
                        flex: 1
                      }}
                    >



                      <div onClick={() => toggleFolderExpand(folder.folderName)} style={{
                        padding: '20px',
                        paddingLeft: '50px',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: 'gold',
                        borderBottom: '2px solid gold',
                        position: 'relative' // Added for absolute positioning of carets
                      }}>
                        {/* Folder reordering carets - outside the folder border */}
                        {folder.folderName !== 'Featured' && folder.folderName !== 'Safe' && (
                          <div style={{
                            position: 'absolute',
                            left: '15px', // Increased to ensure it's outside the container
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0px',
                            color: 'gold',
                            zIndex: 10 // Ensure carets appear above other elements
                          }}>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                changeFolderIndex(folder.folderName, 'up');
                              }}
                              style={{
                                background: 'rgba(0,0,0,0.7)',
                                border: 'none',
                                width: '30px',
                                height: '25px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'gray',
                                fontSize: '30px'
                              }}
                            >
                              <i className="fa-solid fa-caret-up"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                changeFolderIndex(folder.folderName, 'down');
                              }}
                              style={{
                                background: 'rgba(0,0,0,0.7)',
                                border: 'none',
                                width: '30px',
                                height: '25px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'gray',
                                fontSize: '30px'
                              }}
                            >
                              <i className="fa-solid fa-caret-down"></i>
                            </button>
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          {/* Remove the old folder reordering controls */}
                          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '15px' }}>
                            <i
                              className={expandedFolder === folder.folderName ? "fa-solid fa-folder-open" : "fa-solid fa-folder"}
                              style={{ marginRight: '10px', color: 'gold', fontSize: '24px' }}
                            ></i>
                            <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                              {folder.folderName}
                            </span>
                            {folder.isPrivate && (
                              <i className="fa-solid fa-eye-slash" style={{ marginLeft: '10px', color: 'gray', fontSize: '18px' }}></i>
                            )}
                            <span style={{ marginLeft: '10px', color: '#aaa', fontSize: '12px' }}>
                              ({allGames.length} games)
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {/* Privacy toggle with blocked icon for default folders */}
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFolderPrivacy(folder.folderName);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: folder.folderName !== 'Featured' && folder.folderName !== 'Safe' ? 'pointer' : 'not-allowed',
                              marginRight: '15px',
                              position: 'relative'
                            }}
                            className={folder.folderName === 'Featured' || folder.folderName === 'Safe' ? 'default-folder-privacy' : ''}
                          >
                            <input
                              type="checkbox"
                              checked={folder.isPrivate}
                              onChange={() => { }}
                              style={{
                                cursor: folder.folderName !== 'Featured' && folder.folderName !== 'Safe' ? 'pointer' : 'not-allowed',
                                opacity: folder.folderName !== 'Featured' && folder.folderName !== 'Safe' ? 1 : 0.5
                              }}
                              disabled={folder.folderName === 'Featured' || folder.folderName === 'Safe'}
                            />
                            <span style={{
                              marginLeft: '5px',
                              color: folder.folderName !== 'Featured' && folder.folderName !== 'Safe' ? '#ddd' : '#666'
                            }}>
                              Private
                            </span>


                          </div>

                          {/* Trash icon for folder deletion - disable for default folders */}
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              if (folder.folderName !== 'Featured' && folder.folderName !== 'Safe') {
                                setFolderToDelete(folder.folderName);
                                setShowFolderDeleteDialog(true);
                              } else {
                                showNotification(`Cannot delete default folder "${folder.folderName}"`, 'error');
                              }
                            }}
                            style={{
                              cursor: folder.folderName !== 'Featured' && folder.folderName !== 'Safe' ? 'pointer' : 'not-allowed',
                              marginRight: '15px',
                              color: folder.folderName !== 'Featured' && folder.folderName !== 'Safe' ? '#ddd' : '#666'
                            }}
                            className={folder.folderName === 'Featured' || folder.folderName === 'Safe' ? 'default-folder-delete' : ''}
                          >
                            <i className="fa-solid fa-trash-can"></i>


                          </div>

                          <i
                            className={expandedFolder === folder.folderName ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"}
                            style={{ color: '#ddd', fontSize: '16px' }}
                          ></i>
                        </div>
                      </div>
                    </div>

                    {/* Games in Folder - with horizontal scrolling */}
                    {expandedFolder === folder.folderName && (
                      <div style={{
                        padding: '10px 5px',
                        position: 'relative',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                      }}>
                        {/* Left scroll button with className for accessibility */}
                        <button
                          className={`left-scroll-${folder.folderName}`}
                          style={{
                            position: 'absolute',
                            left: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            background: 'rgba(0,0,0,0.5)',
                            border: '2px solid #57b047',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            cursor: 'pointer',
                            color: '#57b047',
                            fontSize: '1.5rem',
                            zIndex: 10000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'opacity 0.3s ease',
                            opacity: 0 // Start hidden, will be updated by effect
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            scrollLeft(`folder-${folder.folderName}`);
                          }}
                        >
                          <i className="fa-solid fa-chevron-left"></i>
                        </button>

                        {/* Container with fixed width to limit visible GameSummaries */}
                        <div style={{
                          margin: '0 auto', // Changed from '0px 30px' to center it
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          {/* Scrollable container for GameSummary components */}
                          <div
                            ref={el => scrollContainerRefs.current[`folder-${folder.folderName}`] = el}
                            style={{
                              display: 'flex',
                              overflowX: 'auto',
                              overflowY: 'hidden',
                              gap: '15px',
                              padding: '10px',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#57b047 transparent',
                              msOverflowStyle: 'auto',
                              maxWidth: 'calc(100% - 60px)',
                              boxSizing: 'border-box'
                            }}
                            onScroll={(e) => {
                              // Force update scroll buttons on scroll events
                              const container = e.currentTarget;
                              const leftArrow = document.querySelector(`.left-scroll-${folder.folderName}`);
                              const rightArrow = document.querySelector(`.right-scroll-${folder.folderName}`);

                              if (container && leftArrow && rightArrow) {
                                const hasOverflow = container.scrollWidth > container.clientWidth;
                                const atLeftEdge = container.scrollLeft === 0;
                                const atRightEdge = Math.abs(container.scrollWidth - container.clientWidth - container.scrollLeft) < 5;

                                // Hide left arrow when at left edge
                                leftArrow.style.opacity = hasOverflow && !atLeftEdge ? 1 : 0;
                                leftArrow.style.pointerEvents = hasOverflow && !atLeftEdge ? 'auto' : 'none';

                                // Hide right arrow when at right edge
                                rightArrow.style.opacity = hasOverflow && !atRightEdge ? 1 : 0;
                                rightArrow.style.pointerEvents = hasOverflow && !atRightEdge ? 'auto' : 'none';
                              }
                            }}
                          >
                            {allGames.length === 0 ? (
                              <div style={{ padding: '20px', color: '#aaa', fontStyle: 'italic' }}>
                                No games in this folder yet.
                              </div>
                            ) : (
                              allGames.map((game, gameIndex) => (
                                <div
                                  key={game.gameId}
                                  ref={el => gameRefs.current[game.gameId] = el}
                                  style={{
                                    flexShrink: 0,
                                    cursor: 'default',
                                    position: 'relative',
                                    transition: 'transform 0.2s, opacity 0.2s',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    padding: '4px 0', // Adding padding to ensure difficulty icons aren't cut off
                                    width: '325px',
                                    maxWidth: '325px',
                                    overflow: 'hidden',
                                    paddingLeft: '25px',
                                  }}
                                >
                                  {/* Game Summary component */}
                                  <GameSummaryWithControls
                                    game={game}
                                    folderName={folder.folderName}
                                    folders={folders}
                                    gameIndex={gameIndex}
                                    onDelete={handleDeleteGame}
                                    onReorder={reorderGames}
                                    onMove={moveGameToFolder}
                                  />
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Right scroll button with className for accessibility */}
                        <button
                          className={`right-scroll-${folder.folderName}`}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            background: 'rgba(0,0,0,0.5)',
                            border: '2px solid #57b047',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            cursor: 'pointer',
                            color: '#57b047',
                            fontSize: '1.5rem',
                            zIndex: 10000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'opacity 0.3s ease',
                            opacity: 0 // Start hidden, will be updated by effect
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            scrollRight(`folder-${folder.folderName}`);
                          }}
                        >
                          <i className="fa-solid fa-chevron-right"></i>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

        </div>

        {/* Add New Folder UI - Single row version */}
        <div style={{
          position: 'absolute',
          bottom: 0,

          borderTop: '1px solid rgba(255,255,255,0.1)',
          padding: '20px 0px',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backdropFilter: 'blur(5px)',
          width: '100%',
        }}>
          {showNewFolderInput ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              maxWidth: '800px',
              width: '100%'
            }}>
              <span style={{
                color: 'gold',
                fontSize: '24px',
                whiteSpace: 'nowrap',
                fontWeight: 'bold'
              }}>
                Name Folder:
              </span>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                style={{
                  flex: 1,
                  backgroundColor: 'black',
                  border: '2px solid gold',
                  color: 'white',
                  borderRadius: '5px',
                  fontSize: '16px',
                  padding: '8px 12px',
                  minWidth: '200px'
                }}
                autoFocus
              />

              <div
                onClick={() => setNewFolderPrivate(!newFolderPrivate)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <input
                  type="checkbox"
                  checked={newFolderPrivate}
                  onChange={() => { }}
                  style={{
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px',
                    marginRight: '8px'
                  }}
                />
                <span style={{ color: '#ddd', fontSize: '16px' }}>
                  Private
                </span>
              </div>

              <button
                onClick={() => {
                  setShowNewFolderInput(false);
                  setNewFolderName('');
                  setNewFolderPrivate(activeTab === 'private');
                }}
                style={{
                  padding: '8px 15px',
                  borderRadius: '5px',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: '1px solid white',
                  whiteSpace: 'nowrap'
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                style={{
                  padding: '8px 15px',
                  borderRadius: '5px',
                  color: 'black',
                  fontSize: '14px',
                  cursor: newFolderName.trim() ? 'pointer' : 'not-allowed',
                  backgroundColor: newFolderName.trim() ? 'gold' : '#555',
                  border: 'none',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                Create Folder
              </button>
            </div>
          ) : (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', maxWidth: '500px', width: '100%' }}>
              <div className="tooltip-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, paddingRight: '20px' }}>
                <i
                  className="fa-solid fa-info-circle"
                  style={{ cursor: 'pointer', color: 'gold', fontSize: '30px', }}
                ></i>
                <span className="tooltip" style={{ textAlign: 'left', fontSize: '20px', paddingBottom: '25px' }}>
                  <bl>
                    <li>Public Bookmarks appear on your Public Profile.</li>
                    <li>Folders and Games appear in the order displayed here.</li><br />
                    <li>Click <span
                      className="game-controls-toggle"

                      style={{

                        cursor: 'pointer',
                        color: 'gold',
                        fontSize: '24px',
                        width: '30px',
                        height: '30px',
                        padding: '10px',

                        borderRadius: '50%',
                        background: 'rgba(0, 0, 0, 0.7)',
                        border: '2px solid gold',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)',
                        zIndex: 5
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateX(-50%) scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.5)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateX(-50%)';
                        e.currentTarget.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
                      }}
                    >
                      <i className="fa-solid fa-ellipsis"></i>
                    </span> below any Game for more options.</li>
                  </bl>
                </span>
              </div>
              <button
                onClick={() => {
                  setShowNewFolderInput(true);
                  setNewFolderPrivate(activeTab === 'private');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: '2px solid gold',
                  borderRadius: '5px',
                  color: 'gold',
                  fontSize: '16px',
                  cursor: 'pointer',
                  gap: '10px',
                  fontWeight: 'bold'
                }}
              >
                <i className="fa-solid fa-plus"></i>
                Create New Folder
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Map Redux state to component props
const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(BookmarksTab);

